import { ValidationKey } from '@/constants/validation-key';
import { validateCity, validateStreetName } from '@/validators/address';
import {
    CountryInstanceValidators,
    ValidationReturn
} from '@/validators/countryInstanceValidators/CountryInstanceValidators';

export class RomaniaInstanceValidators implements CountryInstanceValidators {
    validateBankAccountNumber(accountNumber: string): ValidationReturn {
        const normalized = accountNumber.replace(/[\s-]/g, '').toUpperCase();
        const withoutPrefix = normalized.startsWith('RO') ? normalized.slice(2) : normalized;
        const isValid = this.isRomanianAccountNoValid(withoutPrefix);
        return !isValid ? ValidationKey.BankAccountNumber : undefined;
    }

    private isRomanianAccountNoValid(no: string): boolean {
        if (!no) return false;

        const iban = 'RO' + no;

        if (iban.length !== 24) return false;

        // Move first 4 chars (country code + check digits) to end
        const rearranged = iban.slice(4) + iban.slice(0, 4);

        // Replace letters with numbers: A=10, B=11, ..., Z=35
        const numericString = rearranged
            .split('')
            .map((char) => {
                const code = char.charCodeAt(0);
                return code >= 65 && code <= 90 ? (code - 55).toString() : char;
            })
            .join('');

        // Calculate mod 97 in chunks to handle large numbers
        let remainder = 0;
        for (let i = 0; i < numericString.length; i += 9) {
            const chunk = remainder.toString() + numericString.slice(i, i + 9);
            remainder = parseInt(chunk, 10) % 97;
        }

        return remainder === 1;
    }

    validatePhoneNumber(phoneNumber: string): ValidationReturn {
        const pattern = new RegExp(/^(\d( *)){9}$/, 'g');
        const isValid = pattern.test(phoneNumber);
        return !isValid ? ValidationKey.InvalidPhone : undefined;
    }

    validatePhoneNumberWithPrefix(phoneNumber: string): ValidationReturn {
        const pattern = new RegExp(/^(\+40)( )*(\d( *)){9}$/, 'g');
        const isValid = pattern.test(phoneNumber);
        return !isValid ? ValidationKey.InvalidPhone : undefined;
    }

    validateTaxIdentificationNumber(taxNumber: string): ValidationReturn {
        let cif = taxNumber.trim();

        if (!/^\d+$/.test(cif)) {
            if (cif.toUpperCase().startsWith('RO')) {
                cif = cif.slice(2).trim();
            }
            if (!/^\d+$/.test(cif)) {
                return ValidationKey.InvalidTaxNumber;
            }
        }

        if (cif.length > 10 || cif.length < 6) {
            return ValidationKey.InvalidTaxNumber;
        }

        const digits = cif.split('').map(Number);
        const c1 = digits[digits.length - 1];
        const weights = [7, 5, 3, 2, 1, 7, 5, 3, 2];
        const weightOffset = 9 - (digits.length - 1);

        let t = 0;
        for (let i = 0; i < digits.length - 1; i++) {
            t += digits[i] * weights[weightOffset + i];
        }

        let c2 = (t * 10) % 11;
        if (c2 === 10) c2 = 0;

        return c1 !== c2 ? ValidationKey.InvalidTaxNumber : undefined;
    }

    // eslint-disable-next-line unused-imports/no-unused-vars
    validateInternationalTaxIdentificationNumber(taxNumber?: string): ValidationReturn {
        // not implemented
        return ValidationKey.InvalidInternationalTaxNumber;
    }

    validateZipCode(zipCode: string): ValidationReturn {
        const pattern = /^(0[1-8]|[1-2][0-4]|[3,7][0-3]|[4-5][0-5]|[6,8,9][0-2])[0-7]\d{3}$/;
        const isValid = pattern.test(zipCode);
        return !isValid ? ValidationKey.InvalidZipCode : undefined;
    }

    validateCity(city: string, minLength = 2): ValidationReturn {
        return validateCity(city, minLength);
    }

    validateStreet(street: string, minLength = 2): ValidationReturn {
        return validateStreetName(street, minLength);
    }

    validateBuildingNo(buildingNo: string): ValidationReturn {
        if (buildingNo.startsWith('0')) {
            return ValidationKey.BuildingNoCannotStartsWithZero;
        }
        const VALID_BUILDING_NO_REGEXP = /^[1-9\s/]{0,25}[a-zA-Z]{0,1}[0-9\s/]{0,25}$/;
        const isValid = VALID_BUILDING_NO_REGEXP.test(buildingNo);

        return !isValid ? ValidationKey.BuildingNo : undefined;
    }
}
